import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PROJECT_TYPES = ["Feature Film", "Streaming / Episodic", "Commercial", "Music Video", "Virtual Production", "Other"];
const BUDGETS = ["Under $100K", "$100K – $500K", "$500K – $2M", "$2M – $10M", "$10M+", "Prefer to discuss"];

const inputCls = "w-full bg-white/[0.04] border border-white/15 px-4 py-3 text-zinc-100 placeholder:text-zinc-600 focus:border-amber-500 focus:outline-none transition-colors";
const labelCls = "block font-mono2 text-[10px] tracking-[0.25em] text-zinc-500 uppercase mb-2";

export const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", company: "", project_type: "", budget: "", message: "" });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.project_type || !form.message) {
      toast.error("Please fill in name, email, project type and a brief.");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      setDone(true);
      toast.success("Enquiry received. A producer will reply within one business day.");
    } catch (err) {
      toast.error("Could not send your enquiry. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="py-24 px-4 sm:px-8 lg:px-16 bg-[#0E0E12] border-t border-white/10">
      <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-14">
        <div>
          <p className="font-mono2 text-xs tracking-[0.35em] text-amber-500/90 mb-4">START A PRODUCTION</p>
          <h2 className="font-display text-5xl sm:text-6xl lg:text-7xl tracking-wide text-zinc-50 leading-[0.95]">
            YOUR SCRIPT.<br /><span className="text-stroke">OUR SIMULATION.</span>
          </h2>
          <p className="mt-6 text-zinc-400 leading-relaxed max-w-md">
            Send us the brief, the boards, or just the impossible sentence in your script.
            A VFX supervisor — not a bot — reads every enquiry.
          </p>
          <div className="mt-10 space-y-3 font-mono2 text-xs tracking-[0.2em] text-zinc-500">
            <div>NEW BUSINESS — <span className="text-zinc-200">production@kthstudios.com</span></div>
            <div>BIDS &amp; BREAKDOWNS — <span className="text-zinc-200">bids@kthstudios.com</span></div>
            <div>STUDIOS — <span className="text-zinc-200">CHENNAI | BANGALORE</span></div>
          </div>
        </div>

        {done ? (
          <div data-testid="contact-form-success-message" className="flex flex-col items-center justify-center border border-amber-500/40 bg-amber-500/5 p-12 text-center">
            <CheckCircle2 className="w-14 h-14 text-amber-500 mb-6" />
            <h3 className="font-display text-4xl tracking-wide text-zinc-50">ENQUIRY LOGGED</h3>
            <p className="mt-4 text-zinc-400 max-w-sm">
              Your project is in our pipeline review queue. Expect a producer reply within one business day.
            </p>
            <button data-testid="contact-form-reset-btn" onClick={() => { setDone(false); setForm({ name: "", email: "", company: "", project_type: "", budget: "", message: "" }); }}
              className="mt-8 font-mono2 text-xs tracking-[0.2em] border border-white/20 px-6 py-3 text-zinc-300 hover:border-amber-500 hover:text-amber-500 transition-colors">
              SUBMIT ANOTHER PROJECT
            </button>
          </div>
        ) : (
          <form data-testid="contact-form" onSubmit={submit} className="space-y-5">
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label className={labelCls} htmlFor="cf-name">Name *</label>
                <input id="cf-name" data-testid="contact-form-name-input" className={inputCls} placeholder="Denis Villeneuve"
                  value={form.name} onChange={set("name")} />
              </div>
              <div>
                <label className={labelCls} htmlFor="cf-email">Email *</label>
                <input id="cf-email" data-testid="contact-form-email-input" type="email" className={inputCls} placeholder="producer@studio.com"
                  value={form.email} onChange={set("email")} />
              </div>
            </div>
            <div>
              <label className={labelCls} htmlFor="cf-company">Studio / Company</label>
              <input id="cf-company" data-testid="contact-form-company-input" className={inputCls} placeholder="Studio or production company"
                value={form.company} onChange={set("company")} />
            </div>
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label className={labelCls} htmlFor="cf-type">Project Type *</label>
                <select id="cf-type" data-testid="contact-form-project-type-select" className={inputCls}
                  value={form.project_type} onChange={set("project_type")}>
                  <option value="" className="bg-[#14141A]">Select…</option>
                  {PROJECT_TYPES.map((t) => <option key={t} value={t} className="bg-[#14141A]">{t}</option>)}
                </select>
              </div>
              <div>
                <label className={labelCls} htmlFor="cf-budget">VFX Budget Range</label>
                <select id="cf-budget" data-testid="contact-form-budget-input" className={inputCls}
                  value={form.budget} onChange={set("budget")}>
                  <option value="" className="bg-[#14141A]">Select…</option>
                  {BUDGETS.map((b) => <option key={b} value={b} className="bg-[#14141A]">{b}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label className={labelCls} htmlFor="cf-msg">Project Brief *</label>
              <textarea id="cf-msg" data-testid="contact-form-message-input" rows={5} className={inputCls}
                placeholder="Shot count, target release, the one scene that keeps you up at night…"
                value={form.message} onChange={set("message")} />
            </div>
            <button data-testid="contact-form-submit-button" type="submit" disabled={sending}
              className="flex items-center gap-3 bg-amber-500 text-[#070709] px-8 py-4 font-mono2 text-sm tracking-[0.2em] font-semibold hover:bg-amber-400 disabled:opacity-50 transition-colors">
              {sending ? "SENDING…" : "SEND ENQUIRY"} <Send className="w-4 h-4" />
            </button>
          </form>
        )}
      </div>
    </section>
  );
};
